import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LocalCoordinates } from '@/types/course.types';
import { PlayerInfo } from '@/types/shot.types';
import { BaseShotTracer } from './BaseShotTracer';

/**
 * Shot tracer using Line2 for thick lines
 * This version uses LineGeometry and LineMaterial for pixel-based line width
 */
export class ShotTracer extends BaseShotTracer {
  private lineGeometry: LineGeometry;
  private lineMaterial: LineMaterial;
  private line: Line2;
  private fullPoints: THREE.Vector3[];

  constructor(
    start: LocalCoordinates,
    end: LocalCoordinates,
    apexHeight: number,
    color: number,
    playerInfo: PlayerInfo,
    lineWidth: number = 5
  ) {
    super(start, end, apexHeight, color, playerInfo);

    this.fullPoints = this.curve.getPoints(50);

    // Create Line2 with thick line support
    this.lineGeometry = new LineGeometry();
    this.lineMaterial = new LineMaterial({
      color: color,
      linewidth: lineWidth, // in pixels
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    });

    this.line = new Line2(this.lineGeometry, this.lineMaterial);

    // Initial empty line
    this.updatePath();
  }

  /**
   * Update the line path based on animation progress
   */
  protected updatePath(): void {
    const numPoints = Math.max(
      2,
      Math.floor(this.fullPoints.length * this.animationState.progress)
    );
    const currentPoints = this.fullPoints.slice(0, numPoints);

    // Convert to Line2 format: flat array of [x, y, z, x, y, z, ...]
    const positions: number[] = [];
    currentPoints.forEach((point) => {
      positions.push(point.x, point.y, point.z);
    });

    // Recreate geometry for progressive drawing
    this.lineGeometry.dispose();
    this.lineGeometry = new LineGeometry();
    this.lineGeometry.setPositions(positions);
    this.line.geometry = this.lineGeometry;
    this.line.computeLineDistances();
  }

  /**
   * Get the Line2 object for adding to scene
   */
  public getPathObject(): THREE.Object3D {
    return this.line;
  }

  /**
   * Update line resolution on window resize
   */
  public updateResolution(width: number, height: number): void {
    this.lineMaterial.resolution.set(width, height);
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    this.lineGeometry.dispose();
    this.lineMaterial.dispose();
    this.golfBall.dispose();
  }
}
